import type { ReactNode } from 'react';

import { STATUS, STROKE, SURFACE, TEXT } from '../theme/tokens';

// Shared presentational primitives for every detail surface — tenant (ADR-023),
// group (FD-ADR-030), migration and device (FD-ADR-031).
//
// FD-275 moved this out of `components/tenant/`. It was never tenant-scoped in
// practice: the group pages already imported it from there, which is the kind of
// path that quietly teaches the next person to put a group primitive under
// `tenant/`.
//
// The style is the card-free hairline treatment established by
// MigrationOverviewPage: SectionLabel headings, Fact definition rows, and a
// monospace value for identifiers and timestamps. Separation is 1px strokes and
// fill steps — never a shadow (FD-ADR-031 §2).

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <h2
      className="mb-5 font-display text-[11px] font-semibold uppercase tracking-[0.08em]"
      style={{ color: TEXT.secondary }}
    >
      {children}
    </h2>
  );
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt
        className="font-display text-[11px] font-medium uppercase tracking-[0.06em]"
        style={{ color: TEXT.secondary }}
      >
        {label}
      </dt>
      <dd className="font-display text-sm">{children}</dd>
    </div>
  );
}

// Monospace identifier/timestamp value. Full identifiers are surfaced to
// authenticated admins (ADR-023 §5) — never truncated here. `break-all` because
// a tenant GUID that overflows its column is worse than one that wraps.
export function Mono({ children }: { children: ReactNode }) {
  return (
    <span className="break-all font-mono text-xs" style={{ color: TEXT.primary }}>
      {children}
    </span>
  );
}

/**
 * A bordered, shadow-free panel with a SectionLabel header — the container the
 * conduit and registry-health surfaces are built from.
 *
 * Ported from the prototype's PanelShell. `divide-y` on the body rather than a
 * border per child, so a panel with one fact does not render a stray rule.
 */
export function PanelShell({
  title,
  action,
  children,
}: {
  title: ReactNode;
  /** Optional right-aligned control in the header — a re-check button, a link. */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      className="flex flex-col"
      style={{ backgroundColor: SURFACE.base, border: `1px solid ${STROKE.base}` }}
    >
      <header
        className="flex items-center justify-between gap-3 px-4 pb-2 pt-3"
        style={{ borderBottom: `1px solid ${STROKE.subtle}` }}
      >
        <h2
          className="font-display text-[11px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: TEXT.secondary }}
        >
          {title}
        </h2>
        {action}
      </header>
      <div className="flex flex-col gap-3 px-4 py-3">{children}</div>
    </section>
  );
}

export type HealthTone = 'ok' | 'attention' | 'bad' | 'neutral';

const HEALTH_COLOR: Record<HealthTone, string> = {
  ok: STATUS.success,
  attention: STATUS.warning,
  bad: STATUS.error,
  neutral: STATUS.neutral,
};

/**
 * A single health line: a status dot, a label, and a value.
 *
 * `neutral` is the default and means "not applicable / nothing to report" — NOT
 * a mild problem. A surface that renders every unknown in amber trains operators
 * to ignore amber, which is the colour that most needs to keep working.
 */
export function HealthLine({
  tone = 'neutral',
  label,
  children,
}: {
  tone?: HealthTone;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <span
        aria-hidden="true"
        className="mt-[6px] h-[7px] w-[7px] shrink-0 rounded-full"
        style={{ backgroundColor: HEALTH_COLOR[tone] }}
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <span
          className="font-display text-[11px] font-medium uppercase tracking-[0.06em]"
          style={{ color: TEXT.secondary }}
        >
          {label}
        </span>
        <span className="font-display text-sm" style={{ color: TEXT.primary }}>
          {children}
        </span>
      </div>
    </div>
  );
}
